import { getBrandToken } from "@faire/help-center-utils/serialized-data/getBrand";
import { getRetailerToken } from "@faire/help-center-utils/serialized-data/getRetailer";
import { trackHelpCenterIssueNodeClick } from "@faire/web-api--source/events/helpCenter/click/issueNode";
import { useEffect } from "react";

import { SelfServeContext, SelfServeNode } from "../types";

import { useAsyncDataStatus } from "./useAsyncDataStatus";

/**
 * Calls the async functions to fetch the additional data required by the pete event;
 * The PETE event data is fired when the user clicks on a node.
 */
export const usePeteEventAdditionalDataEffect = (
  context: SelfServeContext,
  node: SelfServeNode | undefined
) => {
  const {
    asyncDataInProgress,
    asyncDataComplete,
    setAsInProgress,
    setAsComplete,
    setAsNotRequestedYet,
  } = useAsyncDataStatus();

  useEffect(() => {
    setAsNotRequestedYet();
  }, [node, setAsNotRequestedYet]);

  useEffect(() => {
    if (asyncDataInProgress || asyncDataComplete) {
      return;
    }
    if (!node) {
      setAsComplete();
      return;
    }

    if (!node.additionalPeteEventData) {
      emitPeteEvent("", node);
      setAsComplete();
      return;
    }
    setAsInProgress();
    invokeAdditionalDataFn(node, context)
      .then((data) => {
        emitPeteEvent(data, node);
      })
      .then(setAsComplete);
  }, [
    asyncDataComplete,
    asyncDataInProgress,
    context,
    node,
    setAsComplete,
    setAsInProgress,
  ]);
};

const emitPeteEvent = (peteEventData: string, node: SelfServeNode) => {
  const brandToken = getBrandToken() ?? "";
  const retailerToken = getRetailerToken() ?? "";
  trackHelpCenterIssueNodeClick(
    brandToken,
    retailerToken,
    peteEventData,
    node.id
  );
};

const invokeAdditionalDataFn = (
  node: SelfServeNode,
  context: SelfServeContext
): Promise<string> => {
  if (!node.additionalPeteEventData) {
    return Promise.resolve("");
  }

  return node.additionalPeteEventData(context);
};
